import { useCallback, useState } from 'react';

import { FilePlus, WandSparkles } from 'lucide-react';
import { useDropzone } from 'react-dropzone';
import { toast } from 'sonner';

import { Button } from './shadcn/button';
import { Popover, PopoverContent, PopoverTrigger } from './shadcn/popover';

interface GenerateDetailsButtonProps {
  isGeneratingDetails: boolean;
  onGenerateDetails: (file: File) => void;
}

export function GenerateDetailsButton({ isGeneratingDetails, onGenerateDetails }: GenerateDetailsButtonProps) {
  const [open, setOpen] = useState(false);

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      if (acceptedFiles.length > 0) {
        const file = acceptedFiles[0];

        // this should be handled by OS file viewer but checking just in case
        if (file.type === 'application/pdf') {
          onGenerateDetails(file);
          setOpen(false);
        } else {
          toast.info('Only PDF files can be processed for automatic detail extraction');
        }
      }
    },
    [onGenerateDetails],
  );

  const maxFiles = 1;
  const maxSize = 20 * 1024 * 1024; // 20MB

  const { getRootProps, getInputProps } = useDropzone({
    multiple: false,
    onDropAccepted: onDrop,
    maxSize,
    maxFiles,
    accept: {
      'application/pdf': ['.pdf'],
    },
  });

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button type="button" variant="primary" disabled={isGeneratingDetails}>
          Generate details from document
          <WandSparkles className="h-4 w-4 text-teal-600" />
        </Button>
      </PopoverTrigger>
      <PopoverContent side="bottom" sideOffset={12} className="w-full max-w-sm">
        <div className="space-y-2">
          <h3 className="text-sm font-medium">Upload Document</h3>
          <p className="text-xs text-gray-600">
            Upload a structured PDF document like a claim form to automatically generate matter details. You'll still be
            able to review and edit the details manually.
          </p>

          <div
            {...getRootProps()}
            className={`relative mt-3 flex flex-col items-center justify-center rounded-lg border-2 border-dashed bg-slate-50 p-4 py-8 ${
              isGeneratingDetails ? 'cursor-wait opacity-60' : 'cursor-pointer'
            }`}
          >
            <input {...getInputProps()} disabled={isGeneratingDetails} />

            <Button disabled={isGeneratingDetails} variant="primary" className="h-auto px-3 font-semibold">
              <FilePlus className="h-4 w-4" />
              Upload file
            </Button>
            <p className="mt-3 text-center text-xs leading-4 text-gray-600">
              {isGeneratingDetails ? 'Extracting matter details...' : 'Or drag here'}
            </p>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
